import type { CompoundEntityRef } from '@backstage/catalog-model';
import type { ReaderState } from '@backstage/plugin-techdocs';
import type { ComponentProps, FC } from 'react';

import { getCompoundEntityRef, parseEntityRef } from '@backstage/catalog-model';
import { ErrorPage } from '@backstage/core-components';
import {
  featureFlagsApiRef,
  useApi,
  useRouteRefParams,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  techdocsPlugin,
  TechDocsReaderProvider,
} from '@backstage/plugin-techdocs';
import {
  TechDocsReaderPageProvider,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { useAsync } from 'react-use';

import { TECHDOCS_EXTERNAL_ANNOTATION } from '../../constants';
import { TechDocsStateIndicator } from '../../vendor/TechDocsStateIndicator';
import { MkDocsIFrame } from './components/MkDocsIFrame/MkDocsIFrame';

type Props = Omit<ComponentProps<typeof MkDocsIFrame>, 'contextPath' | 'path'>;

const MkDocsPageContent: FC<Props & { readerSate: ReaderState }> = ({
  readerSate,
  ...rest
}) => {
  const {
    entityMetadata: { loading: entityMetadataLoading, value: entityMetadata },
  } = useTechDocsReaderPage();

  if (!entityMetadataLoading && !entityMetadata) {
    return <ErrorPage status="404" statusMessage="PAGE NOT FOUND" />;
  }

  if (!entityMetadata || readerSate.state !== 'CONTENT_FRESH') {
    return <TechDocsStateIndicator {...readerSate} />;
  }

  const { kind, name, namespace } = getCompoundEntityRef(entityMetadata);
  return (
    <MkDocsIFrame
      {...rest}
      contextPath={`/static-docs/${namespace}/${kind.toLowerCase()}/${name}`}
      path={`/${readerSate.path}`}
    />
  );
};

export const MkDocsPage = (props: Pick<Props, 'padding'>) => {
  const { kind, name, namespace } = useRouteRefParams(
    techdocsPlugin.routes.docRoot,
  );
  const featureFlagApi = useApi(featureFlagsApiRef);
  const hideHeader = !featureFlagApi.isActive('show-mkdocs-header');

  const catalogApi = useApi(catalogApiRef);

  const { loading, value: entity } = useAsync(
    async () => await catalogApi.getEntityByRef({ kind, name, namespace }),
    [kind, namespace, name, catalogApi],
  );

  if (loading) {
    return null;
  }

  let entityRef: CompoundEntityRef = { kind, name, namespace };
  if (entity?.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]) {
    try {
      entityRef = parseEntityRef(
        entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION],
      );
    } catch {
      // not a fan of this but we don't care if the parseEntityRef fails
    }
  }
  return (
    <TechDocsReaderPageProvider entityRef={entityRef}>
      <TechDocsReaderProvider>
        {readerSate => (
          <MkDocsPageContent
            {...props}
            hideHeader={hideHeader}
            hideTabs
            readerSate={readerSate}
          />
        )}
      </TechDocsReaderProvider>
    </TechDocsReaderPageProvider>
  );
};
